import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatMoney, localized, toCents, type Locale } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink, PeekButton } from '../components/RecordLink.js';
import { SortHeader } from '../components/SortHeader.js';
import { downloadExport } from '../lib/listView.js';

/**
 * Work done and never billed.
 *
 * This screen exists because the number it shows was invisible before: draft invoices were
 * summed into "receivables" alongside real debts, so $36,694 of unbilled work looked like
 * money on its way in. It is not — nobody has been asked to pay it.
 *
 * The screen is a worklist, not a report. Every row is one thing somebody can finish today,
 * ordered oldest first, because age is the entire problem: the oldest draft here is 544 days
 * old, and a bill nobody sent for eighteen months is a bill that will not be paid.
 *
 * **No period here, deliberately.** Every other list opens on a window; this one must not. Age
 * *is* the subject — hiding the oldest drafts behind a default period would hide precisely the
 * rows the screen was built to surface. The endpoint is capped at 300 instead, which is a limit
 * on volume rather than on age.
 *
 * The ordering is applied in the browser rather than by the API, because the whole result set is
 * already here: three hundred rows is a worklist, and a round trip to reverse a column somebody
 * is looking at would be slower than doing it. The export is the opposite — it goes back to the
 * API with the same filters, so the file is the whole set and not the capped view.
 */

type UnbilledRow = {
  id: string;
  number: string | null;
  customerId: string | null;
  customerName: string | null;
  total: string;
  balance: string;
  createdOn: string | null;
  ageDays: number | null;
  jobId: string | null;
  jobNumber: string | null;
  jobStage: { name: Record<string, string>; kind: string } | null;
};

type UnbilledResponse = {
  items: UnbilledRow[];
  total: number;
  totalValue: string;
  readyCount: number;
  readyValue: string;
  oldestDays: number;
  medianDays: number;
};

/** Terminal stages: the work is over, so nothing is stopping the bill going out. */
const FINISHED = new Set(['won', 'lost', 'cancelled']);

/**
 * The screen's sort keys, and what each one is called on the invoice endpoint.
 *
 * They differ because the words differ: this screen asks "how old" and "how big", the invoice
 * list asks for a column. Mapping them here rather than renaming either keeps the export
 * provably the same ordering as the table it came from.
 */
const EXPORT_SORT: Record<'age' | 'value' | 'customer', string> = {
  age: 'created',
  value: 'total',
  customer: 'customer',
};

/** Each key's natural direction, so the first click on a heading does the obvious thing. */
const NATURAL: Record<'age' | 'value' | 'customer', 'asc' | 'desc'> = {
  age: 'desc',
  value: 'desc',
  customer: 'asc',
};

export function UnbilledScreen() {
  const { t: translate, i18n } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';
  const { scope, sort, dir } = useSearch({ from: '/unbilled' });
  const navigate = useNavigate();

  const [exportNote, setExportNote] = useState<{ text: string; failed: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);

  const query = useQuery({
    queryKey: ['invoices', 'unbilled'],
    queryFn: () => request<UnbilledResponse>('/invoices/unbilled?limit=300'),
  });

  const direction = dir ?? NATURAL[sort];
  const all = query.data?.items ?? [];
  const rows = (scope === 'ready' ? all.filter((r) => r.jobStage && FINISHED.has(r.jobStage.kind)) : all)
    .slice()
    .sort((a, b) => {
      const order =
        sort === 'value'
          ? toCents(a.total) - toCents(b.total)
          : sort === 'customer'
            ? (a.customerName ?? '').localeCompare(b.customerName ?? '', locale === 'fr' ? 'fr-CA' : 'en-CA')
            : (a.ageDays ?? 0) - (b.ageDays ?? 0);
      return direction === 'asc' ? order : -order;
    });

  const shownValue = rows.reduce((sum, r) => sum + toCents(r.total), 0);

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }), replace: true });

  const runExport = async () => {
    setExporting(true);
    setExportNote(null);
    try {
      const params = new URLSearchParams({
        status: 'draft',
        sort: EXPORT_SORT[sort],
        dir: direction,
      });
      // The screen's one real filter, expressed server-side so the file matches the table. An
      // export that quietly ignored it would hand somebody a hundred rows they did not ask for.
      if (scope === 'ready') params.set('readyOnly', 'true');

      const outcome = await downloadExport('/invoices/export', params, locale);
      setExportNote({
        text: outcome.truncated
          ? t('list.exportTruncated', { limit: outcome.rows, total: outcome.matched })
          : t('list.exportDone', { count: outcome.rows }),
        failed: outcome.truncated,
      });
    } catch {
      setExportNote({ text: t('list.exportFailed'), failed: true });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="screen">
      <header className="screen__head">
        <div>
          <h1 className="screen__title">{t('unbilled.title')}</h1>
          <p className="unbilled__lede">{t('unbilled.lede')}</p>
        </div>
      </header>

      {/* The three numbers that decide what to do first, and nothing else. */}
      <section className="unbilled__stats" aria-label={t('job.money.summary')}>
        <div className="unbilled__stat">
          <span className="label">{t('unbilled.totalLabel')}</span>
          <strong className="money unbilled__figure">{formatMoney(toCents(query.data?.totalValue ?? '0'), locale)}</strong>
          <span className="unbilled__note">{t('unbilled.draftCount', { count: query.data?.total ?? 0 })}</span>
        </div>

        {/* The actionable subset: the job is finished, so there is nothing to wait for. */}
        <button
          type="button"
          className={`unbilled__stat unbilled__stat--action${scope === 'ready' ? ' unbilled__stat--on' : ''}`}
          onClick={() => setSearch({ scope: scope === 'ready' ? 'all' : 'ready' })}
          aria-pressed={scope === 'ready'}
        >
          <span className="label">{t('unbilled.readyLabel')}</span>
          <strong className="money unbilled__figure">{formatMoney(toCents(query.data?.readyValue ?? '0'), locale)}</strong>
          <span className="unbilled__note">
            {t('unbilled.readyNote', { count: query.data?.readyCount ?? 0 })}
            {scope === 'ready' ? ` · ${t('unbilled.filterOn')}` : ''}
          </span>
        </button>

        <div className="unbilled__stat">
          <span className="label">{t('unbilled.oldestLabel')}</span>
          <strong className="tnum unbilled__figure">{t('unbilled.days', { count: query.data?.oldestDays ?? 0 })}</strong>
          <span className="unbilled__note">{t('unbilled.medianNote', { count: query.data?.medianDays ?? 0 })}</span>
        </div>
      </section>

      <div className="listbar">
        <div className="listbar__row">
          <span className="screen__count">
            {rows.length} · {formatMoney(shownValue, locale)}
          </span>

          {scope === 'ready' && (
            <button type="button" className="listbar__chip" onClick={() => setSearch({ scope: 'all' })}>
              <span>{t('unbilled.readyLabel')}</span>
              <span className="listbar__chipX" aria-hidden="true">
                ×
              </span>
            </button>
          )}

          <button type="button" className="listbar__export" onClick={() => void runExport()} disabled={exporting || rows.length === 0}>
            {exporting ? t('list.exporting') : t('list.export')}
          </button>
        </div>

        {exportNote && (
          <p className={`listbar__note${exportNote.failed ? ' listbar__note--warn' : ''}`} role="status">
            {exportNote.text}
          </p>
        )}
      </div>

      {query.isLoading && <p className="screen__loading">{t('common.loading')}</p>}

      {!query.isLoading && rows.length === 0 && (
        <div className="empty">
          <span className="empty__title">{t('unbilled.emptyTitle')}</span>
          <p className="empty__body">{scope === 'ready' ? t('unbilled.emptyReady') : t('unbilled.emptyAll')}</p>
        </div>
      )}

      {rows.length > 0 && (
        <table className="table table--dense">
          <caption>{t('unbilled.caption')}</caption>
          <thead>
            <tr>
              <SortHeader
                field="age"
                label={t('list.column.ageDays')}
                sort={sort}
                dir={direction}
                onSort={(field, next) => setSearch({ sort: field, dir: next })}
              />
              <SortHeader
                field="customer"
                label={t('list.column.customer')}
                sort={sort}
                dir={direction}
                onSort={(field, next) => setSearch({ sort: field, dir: next })}
              />
              <th scope="col">{t('list.column.job')}</th>
              <th scope="col">{t('job.stage')}</th>
              <SortHeader
                field="value"
                label={t('list.column.total')}
                sort={sort}
                dir={direction}
                onSort={(field, next) => setSearch({ sort: field, dir: next })}
                numeric
              />
              <th scope="col" aria-label={t('list.column.actions')} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const finished = row.jobStage && FINISHED.has(row.jobStage.kind);
              return (
                <tr key={row.id}>
                  <td className="cell cell--activity">
                    <span className={`unbilled__age${(row.ageDays ?? 0) > 90 ? ' unbilled__age--stale' : ''}`}>
                      {row.ageDays === null ? '—' : t('unbilled.days', { count: row.ageDays })}
                    </span>
                    {row.createdOn && (
                      <span className="cell__sub">
                        {new Date(row.createdOn).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA')}
                      </span>
                    )}
                  </td>

                  <td className="cell cell--name">
                    {row.customerId ? (
                      <RecordLink kind="customer" id={row.customerId} className="cell__link">
                        {row.customerName ?? '—'}
                      </RecordLink>
                    ) : (
                      <span className="cell__none">{row.customerName ?? '—'}</span>
                    )}
                    <RecordLink kind="invoice" id={row.id} className="cell__sub">
                      {row.number ? t('unbilled.invoiceNumber', { number: row.number }) : t('unbilled.draftNoNumber')}
                    </RecordLink>
                  </td>

                  <td className="cell">
                    {row.jobId ? (
                      <RecordLink kind="job" id={row.jobId} className="cell__link">
                        {row.jobNumber}
                      </RecordLink>
                    ) : (
                      <span className="cell__none">{row.jobNumber ?? '—'}</span>
                    )}
                  </td>

                  <td className="cell">
                    {row.jobStage ? (
                      <span className={`chip${finished ? '' : ' chip--quiet'}`}>{localized(row.jobStage.name, locale)}</span>
                    ) : (
                      <span className="cell__none">—</span>
                    )}
                  </td>

                  <td className="cell cell--balance numeric">
                    {/* A zero-total draft is an empty shell, not money: shown quietly so it
                        does not compete with the ones worth chasing. */}
                    <span className={toCents(row.total) > 0 ? 'money money--due' : 'money money--settled'}>
                      {formatMoney(toCents(row.total), locale)}
                    </span>
                  </td>

                  <td className="cell" style={{ width: 32 }}>
                    <PeekButton kind="invoice" id={row.id} label={t('invoice.preview')} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p className="unbilled__footnote">
        {t('unbilled.footnote')} <Link to="/invoices">{t('invoice.many')}</Link>.
      </p>
    </div>
  );
}
